import { AIContext, showTextGenerationError } from "contexts/AIContext";
import { useContext } from "react"
import usePostValidator, { getNetworkValidationIndex } from "./usePostValidator";
import { getImageAICaptionURL } from "utils/getImageAICaption";
import { CompanyPromptsContext } from "contexts/CompanyPromptsContext";
import { stripHTMLReplaceWithNewLines } from "utils/stripHTML";

// How many variations one copy request may ask for. Keep in sync with `max_variations` in
// followr-api config/copywriting.php: the API clamps larger requests (the answer has to fit
// the model's output budget), so generateCopies splits bigger batches into parallel chunks.
export const COPY_VARIATIONS_PER_REQUEST = {
  twitter: 5,
  threads: 5,
  bluesky: 5,
  tiktok: 3,
  pinterest: 3,
  instagram: 2,
  facebook: 2,
  linkedin: 1,
  youtube: 1,
  medium: 1,
};

const SOURCE_KINDS = ["text", "webpage", "article", "image_description", "post"];

const isFilledString = (value) => typeof value === "string" && !!value.trim();

const pickRandom = (items = []) => items[Math.floor(Math.random() * items.length)];

/**
 * A generation context is either a string (the user's topic or instruction) or an object
 * `{ brief, source: { kind, content }, link }`. Returns its main text, e.g. to check that
 * there is something to write about or to prompt an image.
 */
export const contextToText = (context) => {
  if (typeof context === "string") return context;
  if (!context || typeof context !== "object") return "";
  return context.brief || context.source?.content || "";
};

/** The context as copy request fields: a string becomes the brief; invalid fields are dropped. */
export const normalizeContext = (context) => {
  if (typeof context === "string") return isFilledString(context) ? { brief: context } : {};
  if (!context || typeof context !== "object") return {};

  const normalized = {};
  if (isFilledString(context.brief)) normalized.brief = context.brief;
  if (SOURCE_KINDS.includes(context.source?.kind) && isFilledString(context.source?.content)) {
    normalized.source = { kind: context.source.kind, content: context.source.content };
  }
  if (isFilledString(context.link)) normalized.link = context.link.trim();
  return normalized;
};

/**
 * Splits text into parts that each measure maxLength or less (string length by default).
 * Each cut goes at the last line break, sentence end or space that fits, and mid-word only
 * when a single word is longer than the limit.
 */
export const splitToMaxLength = (text, maxLength, measure = undefined) => {
  const size = measure || ((s) => s.length);
  if (!text || !(maxLength > 0) || size(text) <= maxLength) return [text];

  const lastBoundary = (head, regex, offset, from, to) => {
    let found = -1;
    for (const match of head.matchAll(regex)) {
      const cut = match.index + offset;
      if (cut >= from && cut <= to) found = cut;
    }
    return found;
  };

  const parts = [];
  let rest = text.trim();
  while (size(rest) > maxLength) {
    // Longest prefix that fits.
    let low = 1;
    let high = rest.length;
    while (low < high) {
      const mid = Math.ceil((low + high) / 2);
      if (size(rest.slice(0, mid)) <= maxLength) low = mid;
      else high = mid - 1;
    }
    const fit = low;
    // One extra character, so a space right after the fitting prefix is a clean cut.
    const head = rest.slice(0, fit + 1);
    const half = Math.floor(fit / 2);

    let cut = lastBoundary(head, /\n/g, 0, half, fit);
    if (cut < 1) cut = lastBoundary(head, /[.!?](?=\s)/g, 1, half, fit);
    if (cut < 1) cut = lastBoundary(head, /\s/g, 0, 1, fit);
    if (cut < 1) {
      cut = fit;
      // Never split an emoji (a surrogate pair) in half.
      const code = rest.charCodeAt(cut - 1);
      if (cut > 1 && code >= 0xd800 && code <= 0xdbff) cut -= 1;
    }
    // Counting can depend on context (X counts any link as 23), so check the part itself.
    while (cut > 1 && size(rest.slice(0, cut).trim()) > maxLength) cut -= 1;

    const part = rest.slice(0, cut).trim();
    if (part) parts.push(part);
    rest = rest.slice(cut).trim();
  }
  if (rest) parts.push(rest);
  return parts;
};

export const useCopyGenerator = () => {

  const { getCopy, ensureOnboarded } = useContext(AIContext);
  const { validationConstants } = usePostValidator();
  const { prompts, defaultPrompts, getRandomPrompt } = useContext(CompanyPromptsContext);

  // The prompt-library text that shapes the post: the selected prompts for the network, else
  // the default ones, else a random one.
  const pickStructure = (network, contextPrompts = []) => {
    let _prompts = prompts || [];

    _prompts = _prompts.filter(e => (contextPrompts || []).some(p => p.id == e.id));
    if (!_prompts?.length) _prompts = defaultPrompts || [];
    _prompts = _prompts.filter(e => e.social_network_type == network);
    if (!_prompts?.length) _prompts = [getRandomPrompt(network)];

    return pickRandom(_prompts)?.prompt || undefined;
  }

  const describeAssets = async (assets = []) => {
    let captions = await Promise.allSettled((assets || []).map((asset) => getImageAICaptionURL(asset?.image?.thumbnail?.url || asset?.image?.url)));
    captions = captions.filter(e => !!e.value).map((e, key) => `Image ${key+1}: ${e.value}`);
    return captions.join("\n");
  }

  /**
   * Writes `quantity` post copies for the network in as few requests as the per-network cap
   * allows (run in parallel). Returns `[{ text, title }]`; a failed request only loses its own
   * copies, and one error toast shows when none came back.
   */
  const generateCopies = async (network, context, { instructions = {}, prompts: contextPrompts = [], imageURLs = [], quantity = 1, offset = 0, avoid = [], maxLength, mediaProductType } = {}) => {
    if (!ensureOnboarded()) return [];

    if (typeof instructions === "string") instructions = { extra_instructions: instructions };
    const { tone, format, cta, extra_instructions } = instructions || {};
    const total = Math.max(1, parseInt(quantity) || 1);
    const firstOffset = Math.max(0, parseInt(offset) || 0);
    maxLength = maxLength ?? validationConstants?.[getNetworkValidationIndex(network, mediaProductType)]?.description?.max_length;

    const avoidCopies = (avoid || []).filter(isFilledString);
    const images = network === "medium"
      ? (imageURLs || []).map(x => x?.url || x).filter(url => typeof url === "string" && /^https?:\/\//i.test(url))
      : [];

    const payload = {
      network,
      ...normalizeContext(context),
      ...(mediaProductType ? { media_product_type: mediaProductType } : {}),
      ...(isFilledString(tone) ? { tone } : {}),
      ...(isFilledString(format) ? { format } : {}),
      ...(isFilledString(cta) ? { cta } : {}),
      ...(isFilledString(extra_instructions) ? { extra_instructions } : {}),
      ...(avoidCopies.length ? { avoid: avoidCopies } : {}),
      ...(images.length ? { image_urls: images } : {}),
      ...(maxLength > 0 ? { max_length: maxLength } : {}),
    };

    const perRequest = COPY_VARIATIONS_PER_REQUEST[network] || 1;
    const chunks = [];
    for (let start = 0; start < total; start += perRequest) {
      chunks.push({ start, size: Math.min(perRequest, total - start) });
    }

    let lastError;
    const results = await Promise.allSettled(chunks.map(({ start, size }) => {
      const structure = pickStructure(network, contextPrompts);
      return getCopy("post", {
        ...payload,
        ...(structure ? { structure } : {}),
        variations: size,
        variation_offset: firstOffset + start,
      }, { silent: true, onError: (err) => { lastError = err; } });
    }));

    const copies = results
      .flatMap(r => r.status === "fulfilled" && Array.isArray(r.value?.posts) ? r.value.posts : [])
      .map(post => ({
        text: typeof post?.text === "string" ? post.text.trim() : "",
        title: typeof post?.title === "string" ? post.title.trim() : "",
      }))
      .filter(post => !!post.text);

    if (!copies.length) {
      showTextGenerationError(lastError || `The AI did not return any copy. Please try again.`, true);
    }

    return copies;
  }

  // maxLength caps every part (the AI does not always follow the instruction, so parts are
  // split again here); measure counts characters the way the post validator does.
  const generateMultipleCopiesFromCopy = async (copy, maxLength = undefined, measure = undefined, network = "twitter") => {
    const response = await getCopy("thread", {
      network,
      text: copy,
      ...(maxLength > 0 ? { max_part_length: maxLength } : {}),
    }, { chargeable: false, silent: true });

    let copies = Array.isArray(response?.items)
      ? response.items.filter(isFilledString).map(item => item.trim())
      : [];
    if (!copies.length) copies = [copy];

    return maxLength ? copies.flatMap(c => splitToMaxLength(c, maxLength, measure)) : copies;
  }

  const generateTitle = async (network, text, kind, extraInstructions = "") => {
    if (!ensureOnboarded()) return "";
    const response = await getCopy("title", {
      network,
      text,
      kind,
      ...(isFilledString(extraInstructions) ? { extra_instructions: extraInstructions } : {}),
    });
    return typeof response?.result === "string" ? response.result.trim() : "";
  }

  const generateCopyFromAssets = async (network, assets, extraInstructions = "") => {
    const content = await describeAssets(assets);
    const [copy] = await generateCopies(
      network,
      { source: { kind: "image_description", content } },
      { instructions: { extra_instructions: extraInstructions } }
    );
    return copy?.text || "";
  }

  const generateTitleFromAssets = async (network, assets, extraInstructions = "") => {
    const content = await describeAssets(assets);
    return generateTitle(network, content, "image_description", extraInstructions);
  }

  const generateCopyFromContext = async (network, context, extraInstructions = "", imageURLs = [], contextPrompts = []) => {
    const [copy] = await generateCopies(network, context, {
      instructions: { extra_instructions: extraInstructions },
      prompts: contextPrompts,
      imageURLs,
    });
    return copy?.text || "";
  }

  const generateTitleFromContext = async (network, context, extraInstructions = "") => {
    return generateTitle(network, stripHTMLReplaceWithNewLines(contextToText(context)), "post", extraInstructions);
  }

  return {
    generateCopies,
    generateCopyFromContext,
    generateTitleFromContext,
    generateCopyFromAssets,
    generateTitleFromAssets,
    generateMultipleCopiesFromCopy
  }
}

export default useCopyGenerator;
